import {
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableCellProps,
    FormControlLabel,
    Switch,
    Alert,
} from "@mui/material";
import Site from "types/Site";
import { SiteLinkButton } from "./SiteLinkButton";
import SiteSchedule from "types/SiteSchedule";
import SchedulePeriods from "./SchedulePeriods";
import { useState } from "react";

const unknownConstant = "N/A";
const twentyforsevenClub = "ARC RS 24/7";

export interface SiteInformationContentProps {
    site: Site;
    showSensitiveData: boolean;
    siteSchedule: SiteSchedule;
}

function TableCellConditonal(
    props: TableCellProps & { condition: unknown | undefined },
) {
    const { condition, ...propsWithoutCondition } = props;
    return condition ? (
        <TableCell {...propsWithoutCondition} />
    ) : (
        <TableCell>&nbsp;</TableCell>
    );
}

export function SiteInformationContent(props: SiteInformationContentProps) {
    const site = props.site;

    const showKeepChangingroomsOpenSection =
        !site.siteInformation?.keepChangingroomsOpen &&
        site.siteInformation?.serviceLevel === twentyforsevenClub;

    const [showInUserLocalTime, setShowInUserLocalTime] =
        useState<boolean>(false);

    return (
        <>
            <Table size="small">
                <TableBody
                    sx={{
                        "& td:first-of-type, & td:nth-of-type(3)": {
                            fontWeight: "bold",
                        },
                        "& td": {
                            borderBottom: "none",
                            verticalAlign: "top",
                            padding: "5px 11px",
                            fontSize: "15px",
                        },
                    }}
                >
                    <TableRow sx={{ margin: 0 }}>
                        <TableCell colSpan={4}>
                            <Alert
                                severity="info"
                                icon={false}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="info"
                                            onChange={() =>
                                                setShowInUserLocalTime(
                                                    !showInUserLocalTime,
                                                )
                                            }
                                        />
                                    }
                                    label=""
                                />
                                <span>
                                    Displaying opening and staffing hours in{" "}
                                    <b>
                                        {showInUserLocalTime ? "your" : "club"}
                                    </b>{" "}
                                    local time
                                </span>
                            </Alert>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Opening Type:</TableCell>
                        <TableCell>
                            {site.openingInformation?.openingType ??
                                unknownConstant}
                        </TableCell>
                        <TableCellConditonal
                            condition={props.showSensitiveData}
                        >
                            Key Safe:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={props.showSensitiveData}
                        >
                            In: {site.siteInformation?.keyVaultCodesInside}
                            <br />
                            Out: {site.siteInformation?.keyVaultCodesOutside}
                        </TableCellConditonal>
                    </TableRow>
                    <TableRow>
                        <TableCellConditonal
                            condition={props.siteSchedule.openingTimes}
                        >
                            Opening hours:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={props.siteSchedule.openingTimes}
                        >
                            <SchedulePeriods
                                zonedTimeRanges={
                                    props.siteSchedule.openingTimes
                                }
                                showInUserLocalTime={showInUserLocalTime}
                            />
                        </TableCellConditonal>

                        <TableCellConditonal
                            condition={props.siteSchedule.staffingTimes}
                        >
                            Staffed hours:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={props.siteSchedule.staffingTimes}
                        >
                            <SchedulePeriods
                                zonedTimeRanges={
                                    props.siteSchedule.staffingTimes
                                }
                                showInUserLocalTime={showInUserLocalTime}
                            />
                        </TableCellConditonal>
                    </TableRow>
                    <TableRow>
                        <TableCell>Phone number Club:</TableCell>
                        <TableCell>
                            {site.siteInformation?.phoneNumber}
                        </TableCell>
                        <TableCellConditonal
                            condition={props.showSensitiveData}
                        >
                            Electronic Key Safe:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={props.showSensitiveData}
                        >
                            {site.siteInformation?.hasElectronicKeySafe !== null
                                ? site.siteInformation?.hasElectronicKeySafe ===
                                  true
                                    ? "Yes"
                                    : "No"
                                : unknownConstant}
                        </TableCellConditonal>
                    </TableRow>
                    <TableRow>
                        <TableCell>Adres:</TableCell>
                        <TableCell>
                            {site.primaryAddress?.fullAddress}
                        </TableCell>
                        <TableCellConditonal
                            condition={props.showSensitiveData}
                        >
                            Fire system type:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={props.showSensitiveData}
                        >
                            {site.siteInformation?.fireSystemType}
                        </TableCellConditonal>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={2} />
                        <TableCellConditonal
                            condition={
                                site.siteInformation
                                    ?.hasAutomaticOpeningCapability
                            }
                        >
                            Automatic club opening:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={
                                site.siteInformation
                                    ?.hasAutomaticOpeningCapability
                            }
                        >
                            Yes
                        </TableCellConditonal>
                    </TableRow>
                    {props.showSensitiveData && (
                        <>
                            <TableRow>
                                <TableCell>Risk profile:</TableCell>
                                <TableCell>
                                    {site.siteInformation?.riskProfile ??
                                        unknownConstant}
                                </TableCell>
                                <TableCellConditonal
                                    condition={
                                        site.siteInformation?.hasSharedEntrance
                                    }
                                >
                                    Shared entrance:
                                </TableCellConditonal>
                                <TableCellConditonal
                                    condition={
                                        site.siteInformation?.hasSharedEntrance
                                    }
                                >
                                    {
                                        site.siteInformation
                                            ?.sharedEntranceInformation
                                    }
                                </TableCellConditonal>
                            </TableRow>
                            <TableRow>
                                <TableCell>Security number:</TableCell>
                                <TableCell>
                                    {
                                        site.siteInformation
                                            ?.phoneNumberSecurityPartner
                                    }
                                </TableCell>
                                <TableCellConditonal
                                    condition={
                                        site.siteInformation
                                            ?.hasDoubleNightEntrance
                                    }
                                >
                                    Double night entrance:
                                </TableCellConditonal>
                                <TableCellConditonal
                                    condition={
                                        site.siteInformation
                                            ?.hasDoubleNightEntrance
                                    }
                                >
                                    {
                                        site.siteInformation
                                            ?.doubleNightEntranceInformation
                                    }
                                </TableCellConditonal>
                            </TableRow>
                            <TableRow>
                                <TableCell>Guard code:</TableCell>
                                <TableCell>
                                    {site.siteInformation?.guardCode}
                                </TableCell>
                                <TableCellConditonal
                                    condition={
                                        site.siteInformation
                                            ?.hasCardReaderFrontDoor
                                    }
                                >
                                    Card reader front door:
                                </TableCellConditonal>
                                <TableCellConditonal
                                    condition={
                                        site.siteInformation
                                            ?.hasCardReaderFrontDoor
                                    }
                                >
                                    Yes
                                </TableCellConditonal>
                            </TableRow>
                        </>
                    )}
                    <TableRow>
                        <TableCellConditonal
                            condition={site.siteInformation?.serviceLevel}
                        >
                            Club type:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={site.siteInformation?.serviceLevel}
                        >
                            {site.siteInformation?.siteType}
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={site.siteInformation?.hasSharedParking}
                        >
                            Shared parking:
                        </TableCellConditonal>
                        <TableCellConditonal
                            condition={site.siteInformation?.hasSharedParking}
                        >
                            {site.siteInformation?.sharedParkingInformation}
                        </TableCellConditonal>
                    </TableRow>
                    {(site.siteInformation?.keyVersion?.length || false) && (
                        <>
                            <TableRow>
                                <TableCell />
                                <TableCell />
                                <TableCell>Different key version:</TableCell>
                                <TableCell>
                                    {site.siteInformation?.keyVersion}
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                    {showKeepChangingroomsOpenSection && (
                        <>
                            <TableRow>
                                <TableCell />
                                <TableCell />
                                <TableCell>Never KCO:</TableCell>
                                <TableCell>Yes</TableCell>
                            </TableRow>
                        </>
                    )}
                    {site.siteInformation?.additionalInformation && (
                        <TableRow>
                            <TableCell>Additional info:</TableCell>
                            <TableCell colSpan={3}>
                                {site.siteInformation?.additionalInformation}
                            </TableCell>
                        </TableRow>
                    )}

                    {site.siteLinks?.map((siteLink, index) => (
                        <TableRow key={`${siteLink.siteId}-${siteLink.key}`}>
                            <TableCell>{index === 0 ? "Links:" : ""}</TableCell>
                            <TableCell colSpan={3}>
                                <SiteLinkButton siteLink={siteLink} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}
